import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Badge, IconButton, useToast, Spinner,
  HStack, VStack, Input, Select, Checkbox, Button, useDisclosure, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl,
  FormLabel, Menu, MenuButton, MenuList, Tooltip, SimpleGrid, Card, CardBody, CardFooter,
  Stack, Flex, Text, MenuItem, Spacer, useBreakpointValue, Icon
} from "@chakra-ui/react";
import { DownloadIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { Link, Navigate } from "react-router-dom";
import { MdAdminPanelSettings, MdMoreVert, MdEdit, MdDelete, MdEmail, MdPerson } from "react-icons/md";
import AuthContext from "../../../contexts/Auth";
import client from "../../../helpers/Client";
import urlToId from "../../../helpers/UrlToId";
import Pagination from "../../../components/pagination/Pagination";
import ExportButton from "../../../components/export/ExportButton";

export default function AdminUsers() {
  const auth = useContext(AuthContext);
  const isSuperUser = auth.data?.user?.is_superuser;
  const toast = useToast();
  const msgToString = (x) => {
    if (x == null) return "";
    if (typeof x === "string") return x;
    if (Array.isArray(x)) return x.map((i) => msgToString(i)).join(", ");
    if (typeof x === "object") {
      try {
        return Object.entries(x)
          .map(([k, v]) => Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${String(v)}`)
          .join(' | ');
      } catch {
        return JSON.stringify(x);
      }
    }
    return String(x);
  };
  const notify = (opts) => {
    const { title, description, ...rest } = opts || {};
    return toast({ title: msgToString(title), description: description !== undefined ? msgToString(description) : undefined, ...rest });
  };
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceRef = useRef(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [ordering, setOrdering] = useState("username");
  const [currentPage, setCurrentPage] = useState(1);
  const [numOfPages, setNumOfPages] = useState(1);
  const [numOfItems, setNumOfItems] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const createDisclosure = useDisclosure();
  const editDisclosure = useDisclosure();
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ username: "", email: "", first_name: "", last_name: "", password: "" });
  const [createPassword2, setCreatePassword2] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [visibleCols, setVisibleCols] = useState({ email: true, is_superuser: true, is_servicer: true, is_technical: true, last_login: true, date_joined: true, is_manager: true });

  // Responsive Check
  const isMobile = useBreakpointValue({ base: true, lg: false });

  // Hooks must be called unconditionally; guard side-effects instead of returning early

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      if (ordering) params.set("ordering", ordering);
      if (searchTerm) params.set("search", searchTerm);
      if (roleFilter === "manager") params.set("is_manager", "true");
      if (roleFilter === "servicer") params.set("is_servicer", "true");
      if (roleFilter === "technical") params.set("is_technical", "true");
      if (roleFilter === "superuser") params.set("is_superuser", "true");
      if (pageSize) params.set("page_size", String(pageSize));
      const { data } = await client.get(`/users/?${params.toString()}`);
      const items = data.results || data;
      setUsers(items);
      setNumOfPages(data.num_of_pages || data.total_pages || 1);
      setNumOfItems(data.num_of_items || data.count || items.length || 0);
      setCurrentPage(data.current_page || page);
    } catch (e) {
      notify({ title: "Kullanıcılar alınamadı", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperUser) return;
    fetchUsers(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperUser, roleFilter, currentPage, ordering, pageSize]);

  const toggleManager = async (user) => {
    try {
      const userId = urlToId(user.url);
      const nextVal = !(user.employee?.is_manager);
      await client.put(`/users/${userId}/set_roles/`, { is_manager: nextVal });
      notify({ title: nextVal ? "Yönetici yapıldı" : "Yönetici kaldırıldı", status: "success" });
      fetchUsers(currentPage);
    } catch (e) {
      notify({ title: "İşlem başarısız", status: "error" });
    }
  };

  const toggleServicer = async (user) => {
    try {
      const userId = urlToId(user.url);
      const nextVal = !(user.is_servicer);
      await client.put(`/users/${userId}/set_roles/`, { is_servicer: nextVal });
      notify({ title: nextVal ? "Servisçi yapıldı" : "Servisçi kaldırıldı", status: "success" });
      fetchUsers(currentPage);
    } catch (e) {
      notify({ title: "İşlem başarısız", status: "error" });
    }
  };

  const toggleTechnical = async (user) => {
    try {
      const userId = urlToId(user.url);
      const nextVal = !(user.is_technical);
      await client.put(`/users/${userId}/set_roles/`, { is_technical: nextVal });
      notify({ title: nextVal ? "Teknik yapıldı" : "Teknik kaldırıldı", status: "success" });
      fetchUsers(currentPage);
    } catch (e) {
      notify({ title: "İşlem başarısız", status: "error" });
    }
  };

  const bulkSetRole = async (key, value) => {
    const ids = Array.from(selected).map(urlToId);
    if (ids.length === 0) return;
    try {
      await Promise.all(
        users
          .filter((u) => ids.includes(urlToId(u.url)))
          .map((u) => client.put(`/users/${urlToId(u.url)}/set_roles/`, {
            is_manager: key === "is_manager" ? value : (u.employee?.is_manager || false),
            is_servicer: key === "is_servicer" ? value : (u.is_servicer || false),
          }))
      );
      notify({ title: "Toplu rol güncellemesi tamamlandı", status: "success" });
      setSelected(new Set());
      fetchUsers(currentPage);
    } catch (error) {
      notify({ title: "Toplu işlem hatası", status: "error" });
    }
  };

  const copySelectedEmails = () => {
    try {
      const emails = users.filter(u => selected.has(u.url)).map(u => u.email).filter(Boolean);
      const text = emails.join(", ");
      if (!text) {
        notify({ title: "Kopyalanacak e-posta yok", status: "info" });
        return;
      }
      navigator.clipboard.writeText(text);
      notify({ title: "E-postalar panoya kopyalandı", status: "success" });
    } catch (e) {
      notify({ title: "Kopyalama başarısız", status: "error" });
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected).map(urlToId);
    if (ids.length === 0) return;
    if (!window.confirm("Seçili kullanıcıları silmek istediğinize emin misiniz?")) return;
    try {
      const targets = users.filter(u => ids.includes(urlToId(u.url)));
      const protectedTargets = targets.filter(u => u.is_superuser || (auth.data?.user?.url && u.url === auth.data.user.url));
      const deletableTargets = targets.filter(u => !u.is_superuser && (!auth.data?.user?.url || u.url !== auth.data.user.url));
      const results = await Promise.allSettled(
        deletableTargets.map(u => client.delete(`/users/${urlToId(u.url)}/`))
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      const protectedCount = protectedTargets.length;
      notify({ title: `Silme tamamlandı`, description: `Başarılı: ${ok}, Hatalı: ${failed}${protectedCount ? `, Korunan: ${protectedCount} (superuser/kendi hesabın)` : ''}`, status: ok && !failed && !protectedCount ? 'success' : 'warning' });
      setSelected(new Set());
      fetchUsers(currentPage);
    } catch (e) {
      notify({ title: "Toplu silme başarısız", status: "error" });
    }
  };

  const handleCreate = async () => {
    if (!form.password || !createPassword2) {
      notify({ title: "Hata", description: "Şifre alanları boş olamaz", status: "error" });
      return;
    }
    if (form.password !== createPassword2) {
      notify({ title: "Hata", description: "Şifreler eşleşmiyor", status: "error" });
      return;
    }
    try {
      await client.post("/users/", form);
      notify({ title: "Kullanıcı oluşturuldu", status: "success" });
      createDisclosure.onClose();
      setForm({ username: "", email: "", first_name: "", last_name: "", password: "" });
      setCreatePassword2("");
      fetchUsers(currentPage);
    } catch (error) {
      const data = error?.response?.data;
      const desc = data && typeof data === "object"
        ? (() => { try { return Object.entries(data).map(([k, v]) => Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${String(v)}`).join(' | '); } catch { return JSON.stringify(data); } })()
        : (error?.message || String(error));
      notify({ title: "Oluşturma başarısız", description: desc, status: "error" });
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({
      username: user.username || "",
      email: user.email || "",
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      password: "",
    });
    editDisclosure.onOpen();
  };

  const handleEdit = async () => {
    try {
      await client.put(`/users/${urlToId(editUser.url)}/`, form);
      notify({ title: "Kullanıcı güncellendi", status: "success" });
      editDisclosure.onClose();
      setEditUser(null);
      fetchUsers(currentPage);
    } catch (error) {
      const data = error?.response?.data;
      const desc = data && typeof data === "object"
        ? (() => { try { return Object.entries(data).map(([k, v]) => Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${String(v)}`).join(' | '); } catch { return JSON.stringify(data); } })()
        : (error?.message || String(error));
      notify({ title: "Güncelleme başarısız", description: desc, status: "error" });
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) return;
    try {
      await client.delete(`/users/${urlToId(user.url)}/`);
      notify({ title: "Kullanıcı silindi", status: "success" });
      fetchUsers(currentPage);
    } catch (error) {
      const data = error?.response?.data;
      const desc = data && typeof data === "object"
        ? (() => { try { return Object.entries(data).map(([k, v]) => Array.isArray(v) ? `${k}: ${v.join(', ')}` : `${k}: ${String(v)}`).join(' | '); } catch { return JSON.stringify(data); } })()
        : (error?.message || String(error));
      notify({ title: "Silme başarısız", description: desc, status: "error" });
    }
  };

  if (!isSuperUser) {
    return <Navigate to="/ana-sayfa/" replace />;
  }

  return (
    <Box p={{ base: 2, md: 6 }}>
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Heading size="md">Kullanıcı Yönetimi</Heading>
        {isMobile && (
          <Button colorScheme="green" size="sm" onClick={createDisclosure.onClose} leftIcon={<Icon as={MdPerson} />}>
            Yeni
          </Button>
        )}
      </Flex>

      <VStack align="stretch" spacing={4} mb={4}>
        <Stack direction={{ base: "column", md: "row" }} spacing={3}>
          <Input
            placeholder="Ara: ad, e-posta..."
            value={searchTerm}
            onChange={(e) => {
              const val = e.target.value;
              setSearchTerm(val);
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
              searchDebounceRef.current = setTimeout(() => {
                setCurrentPage(1);
                fetchUsers(1);
              }, 400);
            }}
          />
          <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} w={{ base: "100%", md: "200px" }}>
            <option value="all">Tümü</option>
            <option value="manager">Yöneticiler</option>
            <option value="servicer">Servisçiler</option>
            <option value="technical">Teknik</option>
            <option value="superuser">Superuser</option>
          </Select>
          <Button colorScheme="blue" onClick={() => { setCurrentPage(1); fetchUsers(1); }} w={{ base: "100%", md: "auto" }}>
            Filtrele
          </Button>
          {!isMobile && (
            <Button colorScheme="green" onClick={createDisclosure.onOpen}>Yeni Kullanıcı</Button>
          )}
          {/* Export to Excel/CSV */}
          <ExportButton
            data={users.map(u => ({
              id: urlToId(u.url),
              username: u.username,
              ad: u.first_name || "",
              soyad: u.last_name || "",
              eposta: u.email || "",
              superuser: u.is_superuser ? "Evet" : "Hayır",
              servisci: u.is_servicer ? "Evet" : "Hayır",
              teknik: u.is_technical ? "Evet" : "Hayır",
              yonetici: (u.employee?.is_manager ? "Evet" : "Hayır"),
              son_giris: u.last_login || "",
              olusturma: u.date_joined || "",
            }))}
            filename="kullanicilar"
            columns={[
              { key: "id", label: "ID" },
              { key: "username", label: "Kullanıcı Adı" },
              { key: "ad", label: "Ad" },
              { key: "soyad", label: "Soyad" },
              { key: "eposta", label: "E-posta" },
              { key: "superuser", label: "Superuser" },
              { key: "servisci", label: "Servisçi" },
              { key: "teknik", label: "Teknik" },
              { key: "yonetici", label: "Yönetici" },
              { key: "son_giris", label: "Son Giriş" },
              { key: "olusturma", label: "Oluşturulma" },
            ]}
            sheetName="Kullanıcılar"
            excelOptions={{ title: "Kullanıcılar" }}
          />

        </Stack>

        {selected.size > 0 && (
          <Box overflowX="auto" pb={2}>
            <HStack spacing={3} minW="max-content">
              <Tooltip label={'Seçilenleri Yönetici Yap'}>
                <Button size="sm" onClick={() => bulkSetRole("is_manager", true)}>Yönetici Yap</Button>
              </Tooltip>
              <Tooltip label={'Seçilenlerden Yönetici Kaldır'}>
                <Button size="sm" onClick={() => bulkSetRole("is_manager", false)}>Yönetici Kaldır</Button>
              </Tooltip>
              <Tooltip label={'Seçilenleri Servisçi Yap'}>
                <Button size="sm" onClick={() => bulkSetRole("is_servicer", true)}>Servisçi Yap</Button>
              </Tooltip>
              <Tooltip label={'Seçilenlerden Servisçi Kaldır'}>
                <Button size="sm" onClick={() => bulkSetRole("is_servicer", false)}>Servisçi Kaldır</Button>
              </Tooltip>
              <Tooltip label={'Seçilenleri Teknik Yap'}>
                <Button size="sm" onClick={() => bulkSetRole("is_technical", true)}>Teknik Yap</Button>
              </Tooltip>
              <Tooltip label={'Seçilenlerden Teknik Kaldır'}>
                <Button size="sm" onClick={() => bulkSetRole("is_technical", false)}>Teknik Kaldır</Button>
              </Tooltip>
              <Tooltip label={'Seçilenleri Sil'}>
                <Button size="sm" colorScheme="red" variant="outline" onClick={() => bulkDelete()}>Sil</Button>
              </Tooltip>

              <Tooltip label={'Seçileni CSV Aktar'}>
                <Button size="sm" variant="outline" onClick={() => exportCsv(users.filter(u => selected.has(u.url)))}>CSV Aktar</Button>
              </Tooltip>
            </HStack>
          </Box>
        )}
      </VStack>

      {loading ? (
        <Spinner />
      ) : isMobile ? (
        // Mobile View
        <SimpleGrid columns={1} spacing={4}>
          <Box mb={2}>
            <Checkbox
              isChecked={users.length > 0 && selected.size === users.length}
              isIndeterminate={selected.size > 0 && selected.size < users.length}
              onChange={(e) => {
                const next = new Set();
                if (e.target.checked) {
                  users.forEach((u) => next.add(u.url));
                }
                setSelected(next);
              }}
            >
              Tümünü Seç
            </Checkbox>
          </Box>
          {users.map((u) => {
            const isSelected = selected.has(u.url);
            return (
              <Card key={u.url} variant="outline" borderColor={isSelected ? "blue.500" : "inherit"} borderWidth={isSelected ? "2px" : "1px"}>
                <CardBody p={4}>
                  <Flex align="start" justify="space-between" mb={2}>
                    <HStack>
                      <Checkbox isChecked={isSelected} onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(u.url); else next.delete(u.url);
                        setSelected(next);
                      }} />
                      <VStack align="start" spacing={0}>
                        <Link to={`/admin/kullanicilar/${urlToId(u.url)}/`}>
                          <Text fontWeight="bold" color="blue.600">{u.first_name} {u.last_name}</Text>
                        </Link>
                        <Text fontSize="sm" color="gray.500">{u.username}</Text>
                      </VStack>
                    </HStack>
                    <Menu>
                      <MenuButton as={IconButton} icon={<MdMoreVert />} size="sm" variant="ghost" />
                      <MenuList>
                        <MenuItem icon={<MdEdit />} onClick={() => openEdit(u)}>Düzenle</MenuItem>
                        <MenuItem icon={<MdDelete />} color="red.500" onClick={() => handleDelete(u)}>Sil</MenuItem>
                        <MenuItem icon={<MdAdminPanelSettings />} onClick={() => toggleManager(u)}>
                          {u.employee?.is_manager ? "Yöneticiliği Kaldır" : "Yönetici Yap"}
                        </MenuItem>
                        <MenuItem icon={<MdAdminPanelSettings />} onClick={() => toggleServicer(u)}>
                          {u.is_servicer ? "Servisçiliği Kaldır" : "Servisçi Yap"}
                        </MenuItem>
                        <MenuItem icon={<MdAdminPanelSettings />} onClick={() => toggleTechnical(u)}>
                          {u.is_technical ? "Teknik Rolü Kaldır" : "Teknik Yap"}
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Flex>

                  {u.email && (
                    <HStack fontSize="sm" color="gray.600" mb={2}>
                      <Icon as={MdEmail} />
                      <Text>{u.email}</Text>
                    </HStack>
                  )}

                  <Flex wrap="wrap" gap={2} mt={2}>
                    {u.is_superuser && <Badge colorScheme="green">Superuser</Badge>}
                    {u.is_servicer && <Badge colorScheme="blue">Servisçi</Badge>}
                    {u.is_technical && <Badge colorScheme="cyan">Teknik</Badge>}
                    {u.employee?.is_manager && <Badge colorScheme="purple">Yönetici</Badge>}
                  </Flex>

                  <Flex justify="space-between" mt={3} fontSize="xs" color="gray.400">
                    <Text>Son Giriş: {u.last_login ? new Date(u.last_login).toLocaleDateString() : "-"}</Text>
                    <Text>Kayıt: {u.date_joined ? new Date(u.date_joined).toLocaleDateString() : "-"}</Text>
                  </Flex>
                </CardBody>
              </Card>
            );
          })}
        </SimpleGrid>
      ) : (
        // Desktop View
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>
                <Checkbox
                  isChecked={users.length > 0 && selected.size === users.length}
                  isIndeterminate={selected.size > 0 && selected.size < users.length}
                  onChange={(e) => {
                    const next = new Set();
                    if (e.target.checked) {
                      users.forEach((u) => next.add(u.url));
                    }
                    setSelected(next);
                  }}
                />
              </Th>
              <Th cursor="pointer" onClick={() => setOrdering(ordering === "first_name" ? "-first_name" : "first_name")}>Ad Soyad</Th>
              {visibleCols.email && (
                <Th cursor="pointer" onClick={() => setOrdering(ordering === "email" ? "-email" : "email")}>E-posta</Th>
              )}
              {visibleCols.is_superuser && (
                <Th cursor="pointer" onClick={() => setOrdering(ordering === "is_superuser" ? "-is_superuser" : "is_superuser")}>Superuser</Th>
              )}
              {visibleCols.is_servicer && (
                <Th cursor="pointer" onClick={() => setOrdering(ordering === "is_servicer" ? "-is_servicer" : "is_servicer")}>Servisçi</Th>
              )}
              {visibleCols.is_technical && (
                <Th cursor="pointer" onClick={() => setOrdering(ordering === "is_technical" ? "-is_technical" : "is_technical")}>Teknik</Th>
              )}
              {visibleCols.last_login && (
                <Th cursor="pointer" onClick={() => setOrdering(ordering === "last_login" ? "-last_login" : "last_login")}>Son Giriş</Th>
              )}
              {visibleCols.date_joined && (
                <Th cursor="pointer" onClick={() => setOrdering(ordering === "date_joined" ? "-date_joined" : "date_joined")}>Kayıt Tarihi</Th>
              )}
              {visibleCols.is_manager && (
                <Th>Yönetici</Th>
              )}
              <Th>Aksiyon</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((u) => {
              const isSelected = selected.has(u.url);
              return (
                <Tr key={u.url}>
                  <Td>
                    <Checkbox isChecked={isSelected} onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(u.url); else next.delete(u.url);
                      setSelected(next);
                    }} />
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Link to={`/admin/kullanicilar/${urlToId(u.url)}/`}>{u.first_name} {u.last_name}</Link>
                    </HStack>
                  </Td>
                  {visibleCols.email && (<Td>{u.email}</Td>)}
                  {visibleCols.is_superuser && (
                    <Td>
                      <Badge colorScheme={u.is_superuser ? "green" : "gray"}>{u.is_superuser ? "Evet" : "Hayır"}</Badge>
                    </Td>
                  )}
                  {visibleCols.is_servicer && (
                    <Td>
                      <Badge colorScheme={u.is_servicer ? "blue" : "gray"}>{u.is_servicer ? "Evet" : "Hayır"}</Badge>
                    </Td>
                  )}
                  {visibleCols.is_technical && (
                    <Td>
                      <Badge colorScheme={u.is_technical ? "cyan" : "gray"}>{u.is_technical ? "Evet" : "Hayır"}</Badge>
                    </Td>
                  )}
                  {visibleCols.last_login && (<Td>{u.last_login ? new Date(u.last_login).toLocaleString() : "-"}</Td>)}
                  {visibleCols.date_joined && (<Td>{u.date_joined ? new Date(u.date_joined).toLocaleString() : "-"}</Td>)}
                  {visibleCols.is_manager && (
                    <Td>
                      <Badge colorScheme={u.employee?.is_manager ? "purple" : "gray"}>{u.employee?.is_manager ? "Evet" : "Hayır"}</Badge>
                    </Td>
                  )}
                  <Td>
                    <IconButton aria-label="Teknik" title="Teknik durumunu değiştir" icon={<MdAdminPanelSettings />} size="sm" mr={2} onClick={() => toggleTechnical(u)} />
                    <IconButton aria-label="Servisçi" title="Servisçi durumunu değiştir" icon={<MdAdminPanelSettings />} size="sm" mr={2} onClick={() => toggleServicer(u)} />
                    <IconButton aria-label="Yönetici" title="Yönetici durumunu değiştir" icon={<MdAdminPanelSettings />} size="sm" mr={2} onClick={() => toggleManager(u)} />
                    <Button size="xs" variant="outline" onClick={() => openEdit(u)}>Düzenle</Button>
                    <Button size="xs" colorScheme="red" variant="outline" ml={2} onClick={() => handleDelete(u)}>Sil</Button>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}

      <Box mt={4}>
        <Pagination path={"admin/kullanicilar"} num_of_items={numOfItems} num_of_pages={numOfPages} currentPage={currentPage} onPageChange={(p) => fetchUsers(p)} />
      </Box>

      {/* Create Modal */}
      <Modal isOpen={createDisclosure.isOpen} onClose={createDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Kullanıcı Oluştur</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Kullanıcı Adı</FormLabel>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>E-posta</FormLabel>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Ad</FormLabel>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Soyad</FormLabel>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Şifre</FormLabel>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Şifre (tekrar)</FormLabel>
                <Input type="password" value={createPassword2} onChange={(e) => setCreatePassword2(e.target.value)} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={createDisclosure.onClose}>İptal</Button>
            <Button colorScheme="blue" onClick={handleCreate}>Oluştur</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editDisclosure.isOpen} onClose={editDisclosure.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Kullanıcı Düzenle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Kullanıcı Adı</FormLabel>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>E-posta</FormLabel>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Ad</FormLabel>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Soyad</FormLabel>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Şifre (değiştirmek için)</FormLabel>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={editDisclosure.onClose}>İptal</Button>
            <Button colorScheme="blue" onClick={handleEdit}>Kaydet</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
const exportCsv = (list) => {
  const headers = ["id", "username", "first_name", "last_name", "email", "is_superuser", "is_servicer", "is_technical", "is_manager", "last_login", "date_joined"];
  const rows = list.map(u => ([
    urlToId(u.url),
    u.username,
    u.first_name || "",
    u.last_name || "",
    u.email || "",
    u.is_superuser ? "1" : "0",
    u.is_servicer ? "1" : "0",
    u.is_technical ? "1" : "0",
    (u.employee?.is_manager ? "1" : "0"),
    u.last_login || "",
    u.date_joined || "",
  ]));
  const csv = [headers.join(","), ...rows.map(r => r.map(x => `"${String(x).replaceAll('"', '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `users_${new Date().toISOString().slice(0, 19)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};